if (!customElements.get('gallery-field-slider')) {
  class GalleryFieldSlider extends HTMLElement {
    static DESKTOP_MQ = '(min-width: 1024px)';
    static AUTOPLAY_DELAY_MS = 2800;
    static SWIPE_THRESHOLD = 40;
    static TRANSITION_MS = 750;

    #getRoot() {
      return this.closest('.gallery-field-slider') || this;
    }

    #getTransitionMs() {
      const duration = getComputedStyle(this.#getRoot()).getPropertyValue('--gfs-transition-duration').trim();
      if (duration.endsWith('ms')) {
        const ms = parseFloat(duration);
        return Number.isFinite(ms) ? ms : GalleryFieldSlider.TRANSITION_MS;
      }
      const seconds = parseFloat(duration);
      return Number.isFinite(seconds) ? seconds * 1000 : GalleryFieldSlider.TRANSITION_MS;
    }

    #initialized = false;
    #index = 0;
    #originalCount = 0;
    #autoplayTimer = null;
    #resizeObserver = null;
    #desktopMq = null;
    #reducedMotionMq = null;
    #pointerStartX = 0;
    #pointerActive = false;
    #infiniteEnabled = false;
    #snapIndex = null;
    #isAnimating = false;
    #transitionTimer = null;
    #onBlockSelect = null;
    #onDesktopChange = null;
    #onReducedMotionChange = null;
    #onTransitionEnd = null;

    connectedCallback() {
      if (this.#initialized) return;
      this.#init();
    }

    disconnectedCallback() {
      this.#destroy();
      this.#initialized = false;
    }

    #init() {
      this.track = this.querySelector('[data-track]');
      this.viewport = this.querySelector('[data-viewport]');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      this.slides = this.track
        ? [...this.track.querySelectorAll('.gallery-field-slider__slide:not(.is-clone)')]
        : [];

      if (!this.track || !this.viewport || this.slides.length < 2) return;

      this.#originalCount = this.slides.length;
      this.#initialized = true;
      this.#index = 0;
      this.track.classList.add('is-initialized');

      this.#desktopMq = window.matchMedia(GalleryFieldSlider.DESKTOP_MQ);
      this.#reducedMotionMq = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.#syncMode();
      this.#bindEvents();

      window.requestAnimationFrame(() => {
        this.#apply(false);
        this.#scheduleAutoplay();
      });

      this.#resizeObserver = new ResizeObserver(() => {
        if (!this.#isAnimating) this.#apply(false);
      });
      this.#resizeObserver.observe(this.viewport);
    }

    #destroy() {
      this.#stopAutoplay();
      this.#clearTransitionTimer();
      this.#removeClones();

      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;

      if (this.track && this.#onTransitionEnd) {
        this.track.removeEventListener('transitionend', this.#onTransitionEnd);
      }

      this.#desktopMq?.removeEventListener('change', this.#onDesktopChange);
      this.#reducedMotionMq?.removeEventListener('change', this.#onReducedMotionChange);
      document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    }

    #isDesktop() {
      return this.#desktopMq?.matches === true;
    }

    #syncMode() {
      if (this.#isDesktop()) {
        this.classList.add('is-desktop');
      } else {
        this.classList.remove('is-desktop');
      }

      this.#setupInfinite();
      this.slides = [...this.track.querySelectorAll('.gallery-field-slider__slide')];
    }

    #createClone(source) {
      const clone = source.cloneNode(true);
      clone.classList.add('is-clone');
      clone.classList.remove('is-center', 'is-adjacent');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-shopify-editor-block');
      return clone;
    }

    #setupInfinite() {
      if (this.#infiniteEnabled) return;

      const originals = [...this.track.querySelectorAll('.gallery-field-slider__slide:not(.is-clone)')];
      this.#originalCount = originals.length;

      for (let i = originals.length - 1; i >= 0; i -= 1) {
        this.track.insertBefore(this.#createClone(originals[i]), originals[0]);
      }

      const middle = [...this.track.querySelectorAll('.gallery-field-slider__slide:not(.is-clone)')];
      for (let i = 0; i < middle.length; i += 1) {
        this.track.appendChild(this.#createClone(middle[i]));
      }

      this.#index = this.#originalCount;
      this.#infiniteEnabled = true;
    }

    #removeClones() {
      if (!this.#infiniteEnabled) {
        this.#index = Math.min(this.#index, this.slides.length - 1);
        return;
      }

      this.track.querySelectorAll('.gallery-field-slider__slide.is-clone').forEach((slide) => slide.remove());
      this.slides = [...this.track.querySelectorAll('.gallery-field-slider__slide')];

      if (this.#originalCount > 0) {
        this.#index = ((this.#index - this.#originalCount) % this.#originalCount + this.#originalCount) % this.#originalCount;
      }

      this.#infiniteEnabled = false;
      this.#snapIndex = null;
    }

    #bindEvents() {
      this.prevButton?.addEventListener('click', () => this.#prev());
      this.nextButton?.addEventListener('click', () => this.#next());

      this.addEventListener('mouseenter', () => {
        if (!this.#isDesktop()) this.#stopAutoplay();
      });

      this.addEventListener('mouseleave', () => {
        if (!this.#isDesktop()) this.#scheduleAutoplay();
      });

      this.track.addEventListener('pointerdown', (event) => {
        if (this.#isDesktop()) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.#pointerActive = true;
        this.#pointerStartX = event.clientX;
        this.track.setPointerCapture(event.pointerId);
      });

      this.track.addEventListener('pointerup', (event) => {
        if (this.#isDesktop() || !this.#pointerActive) return;
        const delta = event.clientX - this.#pointerStartX;
        this.#pointerActive = false;
        if (Math.abs(delta) >= GalleryFieldSlider.SWIPE_THRESHOLD) {
          delta < 0 ? this.#next() : this.#prev();
        }
      });

      this.track.addEventListener('pointercancel', () => {
        this.#pointerActive = false;
      });

      this.#onTransitionEnd = (event) => {
        if (event.target !== this.track || event.propertyName !== 'transform') return;
        this.#onMoveEnd();
      };
      this.track.addEventListener('transitionend', this.#onTransitionEnd);

      this.#onDesktopChange = () => {
        this.#stopAutoplay();
        this.#isAnimating = false;
        this.#snapIndex = null;
        this.#syncMode();
        this.#apply(false);
        this.#scheduleAutoplay();
      };

      this.#onReducedMotionChange = () => this.#scheduleAutoplay();

      this.#desktopMq.addEventListener('change', this.#onDesktopChange);
      this.#reducedMotionMq.addEventListener('change', this.#onReducedMotionChange);

      this.#onBlockSelect = (event) => {
        if (!this.contains(event.target)) return;
        const slide = event.target.closest('.gallery-field-slider__slide:not(.is-clone)');
        if (!slide) return;
        const slideIndex = this.slides.indexOf(slide);
        if (slideIndex >= 0) this.#goTo(slideIndex, true);
      };
      document.addEventListener('shopify:block:select', this.#onBlockSelect);
    }

    #shouldAutoplay() {
      return this.#isDesktop() && this.#reducedMotionMq?.matches !== true;
    }

    #scheduleAutoplay() {
      this.#stopAutoplay();
      if (!this.#shouldAutoplay() || this.#isAnimating) return;
      this.#autoplayTimer = window.setTimeout(() => this.#next(), GalleryFieldSlider.AUTOPLAY_DELAY_MS);
    }

    #stopAutoplay() {
      if (this.#autoplayTimer !== null) {
        window.clearTimeout(this.#autoplayTimer);
        this.#autoplayTimer = null;
      }
    }

    #clearTransitionTimer() {
      if (this.#transitionTimer !== null) {
        window.clearTimeout(this.#transitionTimer);
        this.#transitionTimer = null;
      }
    }

    #armTransitionTimer() {
      this.#clearTransitionTimer();
      this.#transitionTimer = window.setTimeout(() => {
        if (this.#isAnimating) this.#onMoveEnd();
      }, this.#getTransitionMs() + 50);
    }

    #firstOriginal() {
      return this.#originalCount;
    }

    #lastOriginal() {
      return this.#originalCount * 2 - 1;
    }

    #next() {
      if (this.#isAnimating) return;

      if (this.#infiniteEnabled) {
        this.#index += 1;
        if (this.#index > this.#lastOriginal()) {
          this.#snapIndex = this.#firstOriginal();
        }
      } else {
        this.#index = (this.#index + 1) % this.slides.length;
      }

      this.#apply(true);
    }

    #prev() {
      if (this.#isAnimating) return;

      if (this.#infiniteEnabled) {
        this.#index -= 1;
        if (this.#index < this.#firstOriginal()) {
          this.#snapIndex = this.#lastOriginal();
        }
      } else {
        this.#index = (this.#index - 1 + this.slides.length) % this.slides.length;
      }

      this.#apply(true);
    }

    #goTo(index, animate) {
      if (this.#isAnimating) return;
      this.#index = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      this.#snapIndex = null;
      this.#apply(animate);
    }

    #updateClasses() {
      this.slides.forEach((slide, i) => {
        const distance = Math.abs(i - this.#index);
        slide.classList.toggle('is-center', distance === 0);
        slide.classList.toggle('is-adjacent', distance === 1);

        if (slide.classList.contains('is-clone')) {
          slide.setAttribute('aria-hidden', 'true');
          return;
        }

        slide.setAttribute('aria-hidden', distance === 0 ? 'false' : 'true');
      });
    }

    #getCurrentTranslateX() {
      const matrix = new DOMMatrix(getComputedStyle(this.track).transform);
      return matrix.m41;
    }

    #getTargetTranslateX() {
      const slide = this.slides[this.#index];
      if (!slide) return 0;

      void this.track.offsetWidth;

      const viewportWidth = this.viewport.offsetWidth;
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      return viewportWidth / 2 - slideCenter;
    }

    #setTranslateX(value, animate) {
      if (animate) {
        this.track.classList.remove('is-animating');
        void this.track.offsetWidth;
        this.track.classList.add('is-animating');
        this.track.style.transform = `translate3d(${value}px, 0, 0)`;
        this.#armTransitionTimer();
        return;
      }

      this.track.classList.remove('is-animating');
      this.track.style.transform = `translate3d(${value}px, 0, 0)`;
      this.#isAnimating = false;
    }

    #apply(animate) {
      this.#updateClasses();
      void this.track.offsetWidth;

      if (!animate) {
        this.#setTranslateX(this.#getTargetTranslateX(), false);
        this.#onMoveEnd();
        return;
      }

      this.#isAnimating = true;
      this.#stopAutoplay();

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          void this.track.offsetWidth;

          const currentX = this.#getCurrentTranslateX();
          const targetX = this.#getTargetTranslateX();

          if (Math.abs(targetX - currentX) <= 0.5) {
            this.#setTranslateX(targetX, false);
            this.#onMoveEnd();
            return;
          }

          this.#setTranslateX(targetX, true);
        });
      });
    }

    #onMoveEnd() {
      this.#clearTransitionTimer();
      this.#isAnimating = false;
      this.track.classList.remove('is-animating');

      if (this.#snapIndex !== null) {
        this.#index = this.#snapIndex;
        this.#snapIndex = null;
        this.#updateClasses();
        void this.track.offsetWidth;
        this.#setTranslateX(this.#getTargetTranslateX(), false);
      }

      this.#scheduleAutoplay();
    }
  }

  customElements.define('gallery-field-slider', GalleryFieldSlider);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('gallery-field-slider').forEach((node) => {
      node.disconnectedCallback();
      node.connectedCallback();
    });
  });
}
